import OrchestratorAPI from './common.js';
import { type Common } from '../common.js';
import {
  type CreateValues,
  type Kind as Ehr,
  type EhrInstance,
  type EhrInstanceBasic,
  type UpdateValues as EhrInstanceUpdateValues,
  type EhrInstanceWithPracticeIds,
  type FindAllOptions,
} from '../models/orchestrator/ehrInstance.js';
import {
  type DowntimeCreateValues,
  type EhrInstanceDowntime,
  type UpdateValues as EhrInstanceDowntimeUpdateValues,
  type EhrInstanceIsDowntime,
} from '../models/orchestrator/ehrInstanceDowntime.js';
import { type PaginatedQueryOptions } from '../models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from '../models/pagination/PaginationResponse.type.js';

export default class API extends OrchestratorAPI {
  public readonly API_PATH = '/ehr-instances';
  public readonly baseEndpoint;

  constructor(common: Common) {
    super(common);
    this.baseEndpoint = `${this.BASE_URL}${this.API_PATH}`;
  }

  async findAllPaginated(
    options: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<EhrInstanceWithPracticeIds>> {
    const url = this.constructPath('/', options);
    return await this.common.getJSON(url);
  }

  async findAll(
    options: FindAllOptions & { includePracticeIds?: boolean } = {}
  ): Promise<EhrInstanceWithPracticeIds[]> {
    const url = this.constructPath('/', options);
    const { ehrInstances } = await this.common.getJSON(url);
    return ehrInstances.map(this.deserialize);
  }

  async findAllBasic(options: FindAllOptions = {}): Promise<EhrInstanceBasic[]> {
    const url = this.constructPath('/basic', options);
    const { ehrInstances } = await this.common.getJSON(url);
    return ehrInstances.map(this.deserialize);
  }

  async findAllKinds(): Promise<Ehr[]> {
    const url = this.constructPath('/kinds');
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<EhrInstance> {
    const url = this.constructPath(`/${id}`);
    const { ehrInstance } = await this.common.getJSON(url);
    return this.deserialize(ehrInstance);
  }

  async findOneByIdBasic(id: string): Promise<EhrInstanceBasic> {
    const url = this.constructPath(`/${id}/basic`);
    const { ehrInstance } = await this.common.getJSON(url);
    return this.deserialize(ehrInstance);
  }

  async create(values: CreateValues): Promise<EhrInstance> {
    const url = this.constructPath('/');
    const { ehrInstance } = await this.common.postJSON(url, values);
    return this.deserialize(ehrInstance);
  }

  async update(id: string, values: EhrInstanceUpdateValues): Promise<EhrInstance> {
    const url = this.constructPath(`/${id}`);
    const { ehrInstance } = await this.common.patchJSON(url, values);
    return this.deserialize(ehrInstance);
  }

  async getDowntime(id: string): Promise<EhrInstanceIsDowntime> {
    const url = this.constructPath(`/${id}/downtime`);
    return await this.common.getJSON(url);
  }

  async findAllDowntimes(id: string): Promise<EhrInstanceDowntime[]> {
    const url = this.constructPath(`${id}/downtimes`);
    const { downtimes } = await this.common.getJSON(url);
    return downtimes;
  }

  async createDowntime(id: string, values: DowntimeCreateValues): Promise<EhrInstanceDowntime> {
    const url = this.constructPath(`/${id}/downtime`);
    const ehrInstanceDowntime = await this.common.postJSON(url, values);
    return this.deserialize(ehrInstanceDowntime);
  }

  async updateDowntime(
    ehrInstanceId: string,
    downtimeId: string,
    values: EhrInstanceDowntimeUpdateValues
  ): Promise<EhrInstanceDowntime> {
    const url = this.constructPath(`/${ehrInstanceId}/downtime/${downtimeId}`);
    const { ehrInstanceDowntime } = await this.common.patchJSON(url, values);
    return this.deserialize(ehrInstanceDowntime);
  }

  async removeDowntime(ehrInstanceId: string, downtimeId: string): Promise<void> {
    const url = this.constructPath(`/${ehrInstanceId}/downtime/${downtimeId}`);
    return await this.common.deleteJSON(url);
  }
}
